import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { generateRandomString } from 'src/common/helper';
import { stickyCol, stickyHeader } from 'src/components/calendar/styles';
import {
  DatePrintType,
} from 'src/components/calendar/TypeCustom';
import { useStore } from 'src/context/StoreContext';
import DataCalendarTimes from 'src/components/task/surgeryCalendar/DataCalendarTimes';
import DaysCalendarHeaderFirst from 'src/components/calendar/schedule/calendarPartials/DaysCalendarHeaderFirst';
import DaysCalendarHeaderSecond from 'src/components/task/surgeryCalendar/DaysCalendarHeaderSecond';
import PrintDateCalendar from 'src/components/calendar/schedule/PrintDateCalendar';
// import './styles/calendar.css';

const colorBorderTable = '#DDDDDD';

type CalendarProps = {
  dateSelected: string;
  dataCalendar: any;
  isLoading: boolean;
  openFilter: boolean;
  reloadCalendar?: () => void; 
};

const Calendar: React.FC<CalendarProps> = (props) => {
  const storeContext = useStore();
  const store = storeContext.store;

  const [doctorsData, setDoctorsData] = useState<any[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dataPrint, setDataPrint] = useState<DatePrintType>({
    datePrint: '',
    code: '',
  });
  const [waiting, setWaiting] = useState<boolean>(false);

  const dataTimes = useMemo(() => props.dataCalendar.times, [props.dataCalendar.times]);
  const dataDays = useMemo(() => props.dataCalendar.days, [props.dataCalendar.days]);

  const handleOpenDialogDetail = useCallback(
    (id: string, patientid: string) => {},
    []
  );

  const handleOpenDialogAddReserve = useCallback((time: string) => {
  }, []);

  const formatDoctorsData = () => {
    const doctors = props.dataCalendar.doctors;

    const doctorsObject: any = doctors
      ? doctors.reduce((acc: any, doctor: any) => {
          acc[doctor.id] = doctor;
          return acc;
        }, {} as any)
      : {};

    setDoctorsData(doctorsObject);
  };

  const dateSelect = useCallback(
    (date: string) => {
      return props.dateSelected === dayjs(date).format('YYYY-MM-DD');
    },
    [props.dateSelected]
  );

  const handleScroll = (scrollToId: string | null) => {
    if (scrollToId) {
      const element = document.getElementById(scrollToId);

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      } else {
        const observer = new MutationObserver((mutations, obs) => {
          const element = document.getElementById(scrollToId);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'start',
            });
            obs.disconnect();
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }
    }
  };

  const handlePrintDate = useCallback(
    (date: string) => () => {
      setWaiting(true);
      setDataPrint({
        datePrint: date,
        code: generateRandomString(5),
      });
    },
    []
  );

  useEffect(() => {
    if (props.dataCalendar.days.length !== 0) {
      setIsLoading(false);
      formatDoctorsData();
    }

    // eslint-disable-next-line
  }, [props.dataCalendar.days]);

  useEffect(() => {
    handleScroll(props.dateSelected);
  }, [props.dateSelected]);

  return (
    <>
      <Paper
        sx={{
          bgcolor: store.palette.container.paper,
          borderRadius: '16px',
          mt: '10px',
          height: 'calc(100vh - 150px)',
          width: props.openFilter ? 'calc(100vw - 355px)' : 'calc(100vw - 190px)',
          overflow: 'hidden',
        }}
      >
        {!isLoading ? (
          <TableContainer
            sx={{
              boxShadow: 'none',
              '& *': { color: 'primary.dark', fontSize: '12px' },
              width: '100%',
              minWidth: 'calc(100vw - 355px)',
              height: '100%',
            }}
          >
            <Table
              stickyHeader
              sx={{
                '& .MuiTableCell-root.MuiTableCell-head': {
                  borderBottom: `1px solid ${colorBorderTable}`,
                  borderRight: `3px solid ${colorBorderTable}`,
                  fontWeight: 700,
                  fontSize: '14px',
                  p: '0px 8px',
                  overflow: 'auto',
                  '&:first-of-type': {
                    ...stickyCol,
                    zIndex: 3,
                  },
                },
                '& .MuiTableCell-root': {
                  borderRight: `3px solid ${colorBorderTable}`,
                },
                '& .MuiTableCell-root.MuiTableCell-body': {
                  p: '0px 8px',
                  '&:first-of-type': {
                    ...stickyCol,
                  },
                },
              }}
              aria-label='simple table'
            >
              <TableHead>
                <TableRow sx={{ height: '52px', ...stickyHeader }}>
                  <TableCell
                    align='center'
                    // sx={{
                    //   borderRight: props.dataCalendar.days
                    //     && props.dataCalendar.days[0] ? getBorderStyle(props.dataCalendar.days[0])
                    //     : `3px solid red !important`
                    // }}
                  />
                  {dataDays && (
                    <DaysCalendarHeaderFirst
                      daysCal={dataDays}
                      handlePrintDate={handlePrintDate}
                      dateSelect={dateSelect}
                      offPrint={true}
                    />
                  )}
                </TableRow>

                <TableRow sx={{ height: '36px', ...stickyHeader, top: '52px' }}>
                  <TableCell
                    align='center'
                    sx={
                      {
                        // borderRight: props.dataCalendar.days
                        //   && props.dataCalendar.days[0]
                        //   ? getBorderStyle(props.dataCalendar.days[0]) : ''
                      }
                    }
                  />
                  {dataDays && (
                    <DaysCalendarHeaderSecond
                      daysCal={dataDays}
                      doctorsData={doctorsData}
                    />
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {dataTimes && (
                  <DataCalendarTimes
                    daysTimes={dataTimes}
                    handleOpenDialogDetail={handleOpenDialogDetail}
                    handleOpenDialogAddReserve={handleOpenDialogAddReserve}
                  />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Skeleton
            variant='rectangular'
            sx={{ width: '100%', height: 'calc(100vh - 150px)' }}
          />
        )}

        <Box display={'none'}>
          <PrintDateCalendar
            dataCalendar={props.dataCalendar}
            dataPrint={dataPrint}
            onComplate={useCallback(() => setWaiting(false), [])}
          />
        </Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={waiting}
        >
          <CircularProgress color='inherit' />
        </Backdrop>
      </Paper>
    </>
  );
};

export default Calendar;
